/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { StreamingLiveCommerceAddressV2 } from "./StreamingLiveCommerceAddressV2";
import { StreamingLiveCommercePaymentMethodV2 } from "./StreamingLiveCommercePaymentMethodV2";
import { StreamingLiveCommerceItemListV2 } from "./StreamingLiveCommerceItemListV2";
import { StreamingLiveCommerceAmountV2 } from "./StreamingLiveCommerceAmountV2";
import { useI18n } from "hooks/i18n/i18n";
import React, { useEffect } from "react";
import { useSelector } from "store";
import { TextLink } from "ui/TextLink";
import { scrollTop } from "utility/index";

import type { PayType } from "@spwn/types";
import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";
import type { AddressInfo } from "modules/auth";
import { StreamingLiveCommerceCartLayoutV2 } from "./StreamingLiveCommerceCartLayoutV2";

type Props = {
  currentVideoId: string;
  THEOPlayerElementHeight: number;
  pseudoCart: (ProductData & { count: number })[];
  addressInfo: AddressInfo | null;
  paymentMethod: PayType;
  shippingFee: number;
  cartTotalFee: number;
  totalFee: number;
  settlementFee: number;
  isError: boolean;
  isCalculatingShippingFee: boolean;
  selectedCreditCardSeq: string | null;
  selectedSecurityCode: string | null;
  incrementCartItem: (itemData: ProductData) => void;
  decrementCartItem: (itemData: ProductData) => void;
  removeCartItem: (itemData: ProductData) => void;
  closeLiveCommerceCart: () => void;
  proceedToCheckout: () => void;
  setPaymentMethod: (method: PayType) => void;
  setSettlementFee: (fee: number) => void;
  setSelectedCreditCardSeq: (cardSeq: string | null) => void;
  setSelectedSecurityCode: (code: string | null) => void;
};

const StreamingLiveCommerceCartV2: React.FC<Props> = (props) => {
  // PawCharge component supposed to call fetchCardInfo to fetch cardInfoList
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const displayEvent = useSelector((state: Store) => state.event.displayEvent);

  const { t } = useI18n();
  const classesLiveCommerceCartDesign = liveCommerceCartDesign({
    isDarkModeEnabled,
    THEOPlayerElementHeight: props.THEOPlayerElementHeight,
  });
  const { paymentMethod } = props;

  const didMount = () => {
    scrollTop();
  };
  useEffect(didMount, []);

  const removeCartItem = (itemData: ProductData) => {
    props.removeCartItem(itemData);
  };

  const headerLeft = <ArrowBackIosIcon onClick={props.closeLiveCommerceCart} />;
  const headerCenter = (
    <p>
      {t("streaming.commerceCart.total")} : ¥{props.totalFee.toLocaleString()}
    </p>
  );
  const headerRight = (
    <div
      css={classesLiveCommerceCartDesign.confirmButton}
      onClick={props.proceedToCheckout}
    >
      {t("streaming.commerceCart.proceedToCheckout")}
    </div>
  );
  const addressSection = (
    <StreamingLiveCommerceAddressV2 addressInfo={props.addressInfo} />
  );
  const paymentSection = (
    <StreamingLiveCommercePaymentMethodV2
      paymentMethod={paymentMethod}
      selectedCreditCardSeq={props.selectedCreditCardSeq}
      selectedSecurityCode={props.selectedSecurityCode}
      setPaymentMethod={props.setPaymentMethod}
      setSelectedCreditCardSeq={props.setSelectedCreditCardSeq}
      setSelectedSecurityCode={props.setSelectedSecurityCode}
    />
  );
  const cartLabel = t("streaming.commerceCart.order");
  const itemListSection = (
    <StreamingLiveCommerceItemListV2
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      pseudoCart={props.pseudoCart}
      displayEvent={displayEvent}
      removeCartItem={removeCartItem}
    />
  );

  const summarySection = (
    <StreamingLiveCommerceAmountV2
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      isDarkModeEnabled={isDarkModeEnabled}
      cartTotalFee={props.cartTotalFee}
      shippingFee={props.shippingFee}
      settlementFee={props.settlementFee}
      totalFee={props.totalFee}
      isCalculatingShippingFee={props.isCalculatingShippingFee}
    />
  );

  const footerSection = props.isError ? (
    <div css={classesLiveCommerceCartDesign.button}>
      <p>{t("streaming.commerceCart.noticeInvalidAddress")}</p>
      <TextLink href={`https://${window.location.hostname}/account/address`}>
        {t("streaming.commerceCart.editAddress")}
      </TextLink>
    </div>
  ) : null;

  return (
    <StreamingLiveCommerceCartLayoutV2
      isDarkMode={isDarkModeEnabled}
      playerHeight={props.THEOPlayerElementHeight}
      headerLeft={headerLeft}
      headerCenter={headerCenter}
      headerRight={headerRight}
      addressSection={addressSection}
      paymentSection={paymentSection}
      cartLabel={cartLabel}
      itemListSection={itemListSection}
      summarySection={summarySection}
      footerSection={footerSection}
    />
  );
};
type LiveCommerceCartDesignProps = {
  THEOPlayerElementHeight: number;
  isDarkModeEnabled: boolean;
};
const liveCommerceCartDesign = (props: LiveCommerceCartDesignProps) => {
  const confirmButton = css`
    padding: 6px 12px;
    background-color: ${props?.isDarkModeEnabled ? "#000000" : "#ffffff"};
    color: ${props?.isDarkModeEnabled ? "#ffffff" : "#000000"};
    border: ${props?.isDarkModeEnabled ? "none" : "1px solid #000000"};
    font-weight: bold;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
  `;
  const button = css`
    padding: 12px 0;
    p {
      font-size: 14px;
      line-height: 1.5em;
      margin-bottom: 8px;
    }
    a {
      display: block;
      width: 100%;
      padding: 12px 0;
      color: #fff;
      font-size: 14px;
      text-align: center;
      text-decoration: none;
      background-color: #00c2ae;
      border: none;
      outline: none;
      appearance: none;
      cursor: pointer;
      &.disabled {
        background-color: #b1b1b1;
      }
    }
  `;
  return {
    confirmButton,
    button,
  };
};

export default StreamingLiveCommerceCartV2;
